/**
 * The HelloWorld component renders an alert with
 * the package name, version and environment.
 */

// External imports
import React, { useState } from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, IconButton, InputBase, Toolbar, Typography } from '@mui/material';
import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import AccountMenu from '../AccountMenu';
import { createSearchParams, useNavigate } from 'react-router-dom';

// Local imports
// import styles from './index.module.scss';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

// Component props
export interface MenuBarProps extends MuiAppBarProps {
  open?: boolean
}

// Component definition
function MenuBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      navigate({
        pathname: '/search',
        search: createSearchParams({
            name: query,
        }).toString(),
      });
      setQuery('');
    }
  };

  return (
    <Box sx={{ display: 'fixed', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <AppBar component="nav" position="fixed">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="open drawer"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}
          >
            {t('menu-bar-title')}
          </Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder={t('search-placeholder')}
              inputProps={{ 'aria-label': 'search' }}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              onKeyDown={handleKeyDown}
            />
          </Search>

          {/* User Menu */}
          <AccountMenu />
        </Toolbar>
      </AppBar>
    </Box>
  );
}

// Default props
MenuBar.defaultProps = {};

// Default export
export default MenuBar;
